import SysRecord from "app/class/record";

export interface NormalizedActionHotkey {
    key: string;
    ctrl: boolean;
    meta: boolean;
    alt: boolean;
    shift: boolean;
    usesMod: boolean;
}

const MOD_ALIASES = new Set(["mod", "cmdorctrl", "commandorcontrol"]);
const CTRL_ALIASES = new Set(["ctrl", "control"]);
const META_ALIASES = new Set(["meta", "cmd", "command"]);
const ALT_ALIASES = new Set(["alt", "option"]);
const SHIFT_ALIASES = new Set(["shift"]);

const KEY_ALIASES: Record<string, string> = {
    esc: "escape",
    return: "enter",
    del: "delete",
    space: " ",
    plus: "+",
};

const DISPLAY_KEY_LABELS: Record<string, string> = {
    " ": "Space",
    arrowdown: "Arrow Down",
    arrowleft: "Arrow Left",
    arrowright: "Arrow Right",
    arrowup: "Arrow Up",
    backspace: "Backspace",
    delete: "Delete",
    enter: "Enter",
    escape: "Esc",
    tab: "Tab",
};

export const getActionShortcut = (action: SysRecord) => {
    const value = action.keyboard_shortcut;
    return typeof value === "string" ? value.trim() : "";
};

export const normalizeActionHotkey = (value: unknown): NormalizedActionHotkey | null => {
    if (typeof value !== "string") return null;

    const parts = value
        .trim()
        .toLowerCase()
        .split("+")
        .map((part) => part.trim())
        .filter(Boolean);

    if (!parts.length) return null;

    let key = "";
    let ctrl = false;
    let meta = false;
    let alt = false;
    let shift = false;
    let usesMod = false;

    parts.forEach((part) => {
        if (MOD_ALIASES.has(part)) {
            usesMod = true;
            return;
        }

        if (CTRL_ALIASES.has(part)) {
            ctrl = true;
            return;
        }

        if (META_ALIASES.has(part)) {
            meta = true;
            return;
        }

        if (ALT_ALIASES.has(part)) {
            alt = true;
            return;
        }

        if (SHIFT_ALIASES.has(part)) {
            shift = true;
            return;
        }

        key = KEY_ALIASES[part] || part;
    });

    if (!key) return null;

    return { key, ctrl, meta, alt, shift, usesMod };
};

export const hotkeyToSignature = (hotkey: NormalizedActionHotkey) => {
    return [
        hotkey.usesMod ? "mod" : "",
        hotkey.ctrl ? "ctrl" : "",
        hotkey.meta ? "meta" : "",
        hotkey.alt ? "alt" : "",
        hotkey.shift ? "shift" : "",
        hotkey.key,
    ].filter(Boolean).join("+");
};

export const isMacPlatform = () => {
    if (typeof window === "undefined") return false;
    return /mac|iphone|ipad|ipod/i.test(window.navigator.platform);
};

export const formatActionShortcut = (value: unknown) => {
    const hotkey = normalizeActionHotkey(value);
    if (!hotkey) return "";

    const labels: string[] = [];

    if (hotkey.usesMod) {
        labels.push(isMacPlatform() ? "Cmd" : "Ctrl");
    }

    if (hotkey.ctrl) labels.push("Ctrl");
    if (hotkey.meta) labels.push(isMacPlatform() ? "Cmd" : "Meta");
    if (hotkey.alt) labels.push(isMacPlatform() ? "Option" : "Alt");
    if (hotkey.shift) labels.push("Shift");

    labels.push(DISPLAY_KEY_LABELS[hotkey.key] || hotkey.key.toUpperCase());

    return labels.join(" + ");
};

export const eventMatchesActionHotkey = (event: KeyboardEvent, hotkey: NormalizedActionHotkey) => {
    const key = KEY_ALIASES[event.key.toLowerCase()] || event.key.toLowerCase();
    const modPressed = isMacPlatform() ? event.metaKey : event.ctrlKey;

    return key === hotkey.key &&
        event.altKey === hotkey.alt &&
        event.shiftKey === hotkey.shift &&
        (hotkey.usesMod ? modPressed : event.ctrlKey === hotkey.ctrl && event.metaKey === hotkey.meta);
};

export const isEditableHotkeyTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;

    const tagName = target.tagName.toLowerCase();
    return target.isContentEditable ||
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select";
};
